import { useEffect, useState } from 'react';
import { ShieldCheck, BedDouble, ClipboardCheck, Heart, Users, Award, Phone, FileText, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Reveal } from '@/components/ui/Reveal';
import { Button } from '@/components/ui/button';
import CPAMBadge from '@/components/ui/CPAMBadge';
import EquipementsModernesSection from '@/components/sections/EquipementsModernesSection';
import { useSEO } from '@/lib/seo';
import api from '@/lib/api';

export default function TransportsSpecialises() {
  useSEO({
    title: "Transports spécialisés | Bariatrique, lit hospitalier | Groupe Ambulances Beaulon",
    description: "Transport bariatrique jusqu'à 500 kg, transport avec lit hospitalier, transferts complexes. Matériel adapté et accompagnement digne dans l'Allier.",
    keywords: "transport bariatrique allier, transport patient obèse, transport lit hospitalier, transferts complexes, ambulance spécialisée"
  });

  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    api.get('/gallery', { params: { category: 'bariatrique' } }).then(r => setPhotos(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <section className="hero-wash">
        <div className="container-app pt-14 pb-10 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <p className="badge-soft">Transports spécialisés</p>
                <CPAMBadge variant="solid" size="sm" />
              </div>
              <h1 className="h1 text-anthracite-950">Un accompagnement digne, en toute sécurité</h1>
              <p className="lead mt-5 max-w-xl">
                Nos équipes prennent en charge les transports les plus complexes avec un même engagement : confort, sécurité et respect absolu de la personne.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/devis">
                  <Button data-testid="specialise-quote-cta"><FileText className="h-4 w-4" /> Demander un devis</Button>
                </Link>
                <a href="tel:0470427263" className="inline-flex items-center gap-2 rounded-md border bg-white px-4 py-2 text-sm font-medium hover:bg-anthracite-100"><Phone className="h-4 w-4" /> Appeler</a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border bg-white shadow-card">
              <img src="https://images.pexels.com/photos/6749778/pexels-photo-6749778.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200" alt="Transport spécialisé - matériel adapté" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Trois piliers */}
      <section className="section-pad">
        <div className="container-app">
          <div className="grid gap-4 md:grid-cols-3">
            {[
              { icon: Heart, title: 'Dignité', text: "Une approche humaine, respectueuse et discrète à chaque étape du transport." },
              { icon: ShieldCheck, title: 'Sécurité', text: "Véhicules adaptés, matériel spécifique et procédures rigoureuses pour assurer un trajet sans risque." },
              { icon: Award, title: 'Confort', text: "Le confort du patient est notre priorité : installation pensée, accompagnement constant." },
            ].map((p, i) => (
              <Reveal key={p.title} delay={i * 80}>
                <div className="card-base p-6 h-full">
                  <div className="h-11 w-11 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center">
                    <p.icon className="h-5 w-5" />
                  </div>
                  <h3 className="h3 mt-4 text-anthracite-950">{p.title}</h3>
                  <p className="mt-2 text-sm text-anthracite-700">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Spécialités détaillées */}
      <section className="section-pad bg-anthracite-100/60">
        <div className="container-app space-y-12">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <Reveal>
              <div>
                <div className="flex items-center gap-2 mb-3"><ShieldCheck className="h-5 w-5 text-brand-600" /><span className="text-sm font-medium text-brand-700">Transport bariatrique</span></div>
                <h2 className="h2 text-anthracite-950">Un transport spécialisé jusqu'à 500 kg</h2>
                <p className="lead mt-3">Nos équipes mobilisent un matériel adapté (brancards renforcés, sangles, dispositifs de levage) et une coordination logistique spécifique pour assurer un transport en toute sécurité et avec une dignité absolue.</p>
                <ul className="mt-5 space-y-2 text-sm text-anthracite-800">
                  <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-600"></span>Matériel certifié et conforme</li>
                  <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-600"></span>Équipes formées aux manipulations spécifiques</li>
                  <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-600"></span>Discrétion et respect en toute circonstance</li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border bg-white shadow-card">
                <img src="https://images.pexels.com/photos/7088837/pexels-photo-7088837.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200" alt="Transport bariatrique adapté" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <Reveal>
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border bg-white shadow-card order-2 lg:order-1">
                <img src="https://images.pexels.com/photos/6754159/pexels-photo-6754159.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200" alt="Transport avec lit hospitalier" className="w-full h-full object-cover" />
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="order-1 lg:order-2">
                <div className="flex items-center gap-2 mb-3"><BedDouble className="h-5 w-5 text-brand-600" /><span className="text-sm font-medium text-brand-700">Transport avec lit hospitalier</span></div>
                <h2 className="h2 text-anthracite-950">La continuité des soins, en mouvement</h2>
                <p className="lead mt-3">Pour certains patients, le transport doit s'effectuer dans leur lit hospitalier. Nos véhicules aménagés permettent d'assurer cette continuité dans des conditions optimales.</p>
              </div>
            </Reveal>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <Reveal>
              <div>
                <div className="flex items-center gap-2 mb-3"><ClipboardCheck className="h-5 w-5 text-brand-600" /><span className="text-sm font-medium text-brand-700">Transferts complexes</span></div>
                <h2 className="h2 text-anthracite-950">Une coordination rigoureuse</h2>
                <p className="lead mt-3">Pour les transferts inter-hôpitaux ou les sorties d'hospitalisation sensibles, nous coordonnons avec les équipes médicales pour garantir un transport fluide et sécurisé.</p>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border bg-white shadow-card">
                <img src="https://images.pexels.com/photos/4225920/pexels-photo-4225920.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200" alt="Matériel professionnel" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Galerie spécialisée */}
      {photos.length > 0 && (
        <section className="section-pad">
          <div className="container-app">
            <Reveal><h2 className="h2 text-anthracite-950 mb-8">Matériel spécialisé</h2></Reveal>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {photos.map(p => (
                <figure key={p.id} className="relative aspect-[4/3] rounded-2xl overflow-hidden border bg-white shadow-card">
                  <img src={p.url} alt={p.title || 'Matériel spécialisé'} className="w-full h-full object-cover" loading="lazy" />
                  {p.title && <figcaption className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/70 to-transparent text-white text-sm">{p.title}</figcaption>}
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Section Équipements modernes (HoverJack & matériel spécialisé) */}
      <EquipementsModernesSection />

      {/* CTA Final */}
      <section className="section-pad">
        <div className="container-app">
          <div className="rounded-3xl bg-gradient-to-r from-brand-700 to-brand-900 text-white p-8 sm:p-12">
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div>
                <h3 className="text-2xl sm:text-3xl font-heading font-semibold">Un transport spécialisé à organiser ?</h3>
                <p className="mt-2 text-white/80 max-w-xl">Nos coordinateurs prennent le temps d'écouter votre situation pour proposer le dispositif le plus adapté.</p>
              </div>
              <div className="flex flex-wrap gap-3 md:justify-end">
                <a href="tel:0470427263" className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-brand-800 font-medium hover:bg-anthracite-100"><Phone className="h-4 w-4" /> Appeler</a>
                <Link to="/devis" className="inline-flex items-center gap-2 rounded-xl bg-brand-500 hover:bg-brand-600 px-5 py-3 text-white font-medium"><FileText className="h-4 w-4" /> Demande de devis <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
